"use client";

import React, { useCallback, useState } from "react";
import { DropdownMenu } from "@/components";
import { HmcButton, HmcNavLink } from "@adi-ctx/harmonic-core-components-react";

import { type TDropdownMenuItem } from "@/components";

type DropdownProps = {
	readonly menuItems: TDropdownMenuItem[];
	readonly orientation?: "left" | "right";
	readonly selected?: TDropdownMenuItem;
	readonly onSelection: (item: TDropdownMenuItem) => void;
};
/**
 * Attach a DropdownMenu component to the Base component
 *
 * Example:
 * const WithDropdownMenu = withDropdownMenu(BaseComponent);
 * <WithDropdownMenu menuItems=[] selected={} onSelect={}, onClose={} />
 *
 * @param BaseComponent
 */
export default function withDropdownMenu<T>(
	BaseComponent: React.ComponentType<T>
) {
	function NewComponent({
		menuItems,
		selected,
		onSelection,
		orientation,
		...props
	}: DropdownProps & T) {
		const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);

		const getOrientation = (orientation?: "left" | "right") =>
			orientation ?? "left";
		const toggleDropdownMenu = useCallback(() => {
			setIsDropdownMenuOpen(!isDropdownMenuOpen);
		}, [setIsDropdownMenuOpen, isDropdownMenuOpen]);

		return (
			<div className="relative">
				<BaseComponent
					{...(props as T)}
					className={
						isDropdownMenuOpen ? "dropdown-menu--open" : "dropdown-menu--closed"
					}
					onClick={toggleDropdownMenu}
				/>

				{isDropdownMenuOpen && (
					<DropdownMenu
						isOpen
						items={menuItems}
						orientation={getOrientation(orientation)}
						onClose={toggleDropdownMenu}
						selected={selected}
						onSelect={onSelection}
					/>
				)}
			</div>
		);
	}

	return NewComponent;
}
